import {
  Box, Tooltip, SpeedDialAction, SpeedDialIcon, SpeedDial,
  Dialog, DialogTitle, DialogContentText, DialogContent, DialogActions,
  Button, Snackbar, Alert, Chip, Paper, Typography
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PullToRefresh } from '../general/PullToRefresh';
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import ArticleIcon from '@mui/icons-material/Article';
import EggAltIcon from '@mui/icons-material/EggAlt';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import IngredientsPage from './IngredientsPage';
import InstructionsPage from './InstructionsPage';
import { deleteRecipeById, getRecipeById, getRecipeImage } from '../../utils/RecipeService';
import { getCurrentUser } from '../../utils/AccountService';
import { Recipe } from '../../models/Recipe';
import { User } from '../../models/User';
import ConditionalLink from './ConditionalLink';
import TagView from '../general/TagView';
import ShareRecipeDialog from './edit/ShareRecipeDialog';

const PLACEHOLDER = 'https://wiki.dave.eu/images/4/47/Placeholder.png';

const useWakeLock = () => {
  const [wakeLock, setWakeLock] = useState<any>(null);
  useEffect(() => {
    let lock: any;
    const requestWakeLock = async () => {
      if ("wakeLock" in navigator) {
        try {
          const wl: any = navigator.wakeLock;
          lock = await wl.request("screen");
          setWakeLock(lock);
          lock.addEventListener("release", () => setWakeLock(null));
        } catch (_) {}
      }
    };
    requestWakeLock();
    return () => { if (lock) lock.release(); };
  }, []);
  return wakeLock;
};

const RecipeDetails = () => {
  let { recipeId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [recipe, setRecipe] = useState<Recipe | null>();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [recipeImage, setRecipeImage] = useState<string>();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [editable, setEditable] = useState(false);
  const [sharedUsers, setSharedUsers] = useState<string[]>([]);
  const [currentUsername, setCurrentUsername] = useState<string>();
  const [checkedIngredients, setCheckedIngredients] = useState<number[]>([]);
  const [completedInstructions, setCompletedInstructions] = useState<number[]>([]);
  const [portions, setPortions] = useState<number>(1);

  useWakeLock();

  const hours = recipe?.cookTime ? String(Math.floor(recipe.cookTime / 60)).padStart(2, '0') : '00';
  const minutes = recipe?.cookTime ? String(recipe.cookTime % 60).padStart(2, '0') : '00';

  const loadRecipe = useCallback(() => {
    return Promise.all([
      getRecipeById(recipeId).then((r: Recipe) => {
        setRecipe(r);
        setEditable(!r.shared!);
        setSharedUsers(r.sharedUsers ?? []);
        setPortions(r.yield ?? 1);
      }),
      getRecipeImage(recipeId as any as number).then((image: Blob | null) => {
        if (image) setRecipeImage(URL.createObjectURL(image));
      }),
      getCurrentUser().then((user: User | null) => setCurrentUsername(user?.username)),
    ]).then(() => {});
  }, [recipeId]);

  useEffect(() => { loadRecipe(); }, [loadRecipe]);

  const deleteRecipe = useCallback(() => {
    setConfirmDialogOpen(false);
    if (recipeId !== undefined) {
      deleteRecipeById(recipeId as any as number).then(response => {
        if (!response.ok) setSnackbarOpen(true);
        else navigate('/');
      });
    }
  }, [navigate, recipeId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Scrollable content area */}
      <PullToRefresh onRefresh={loadRecipe} style={{ flex: 1 }} contentStyle={{ paddingBottom: 80 }}>
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Hero image */}
            <Box
              component="img"
              src={recipeImage ?? PLACEHOLDER}
              alt={recipe?.name}
              sx={{
                width: '100%',
                maxWidth: 600,
                height: { xs: 220, sm: 300 },
                objectFit: 'cover',
                display: 'block',
              }}
            />

            {/* Info card */}
            <Box sx={{ width: '100%', maxWidth: 600, px: 2, mt: 2 }}>
              {/* Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="h5" fontWeight={800} sx={{ flex: 1 }}>
                  {recipe?.name}
                </Typography>
                {recipe?.shared && (
                  <Tooltip title={t('shared')}>
                    <ShareIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </Tooltip>
                )}
                {recipe?.ownerUsername && currentUsername && recipe.ownerUsername !== currentUsername && (
                  <Tooltip title={`${t('ownedBy')}: ${recipe.ownerUsername}`}>
                    <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  </Tooltip>
                )}
              </Box>

              {/* Info chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  icon={<AccessTimeIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${hours}:${minutes}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, borderColor: 'primary.light', color: 'primary.dark' }}
                />
                <Chip
                  icon={<RestaurantIcon sx={{ fontSize: '16px !important' }} />}
                  label={`${recipe?.yield} ${t('portions')}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, borderColor: 'primary.light', color: 'primary.dark' }}
                />
                {recipe?.difficulty && (
                  <Chip
                    icon={<SignalCellularAltIcon sx={{ fontSize: '16px !important' }} />}
                    label={t(recipe.difficulty.toLowerCase())}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, borderColor: 'primary.light', color: 'primary.dark' }}
                  />
                )}
                {recipe?.source && (
                  <Chip
                    icon={<LinkIcon sx={{ fontSize: '16px !important' }} />}
                    label={<ConditionalLink source={recipe.source} />}
                    size="small"
                    variant="outlined"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontWeight: 600, borderColor: 'divider', color: 'text.secondary' }}
                  />
                )}
              </Box>

              {/* Tags */}
              <TagView recipeId={recipe?.id!} tags={recipe?.tags} editable={editable} />
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <IngredientsPage
            basePortions={recipe?.yield ?? 1}
            portions={portions}
            setPortions={setPortions}
            ingredients={recipe?.ingredients ?? []}
            checkedIngredients={checkedIngredients}
            changeCheckedIngredients={setCheckedIngredients}
          />
        )}

        {tab === 2 && (
          <InstructionsPage
            instructions={recipe?.instructions ?? []}
            completedInstructions={completedInstructions}
            changeCompletedInstructions={setCompletedInstructions}
          />
        )}
      </PullToRefresh>

      {/* Speed dial for edit/delete/share */}
      {editable && (
        <SpeedDial
          ariaLabel="recipe actions"
          direction="left"
          sx={{ position: 'fixed', bottom: 80, right: 24 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction key='edit' icon={<EditIcon />} tooltipTitle={t('edit')} onClick={() => navigate('/recipe/edit/' + recipeId)} />
          <SpeedDialAction key='delete' icon={<DeleteIcon />} tooltipTitle={t('deleteRecipe')} onClick={() => setConfirmDialogOpen(true)} />
          <SpeedDialAction key='share' icon={<ShareIcon />} tooltipTitle={t('share')} onClick={() => setShareDialogOpen(true)} />
        </SpeedDial>
      )}

      {/* Bottom tab navigation */}
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }} elevation={3}>
        <BottomNavigation showLabels value={tab} onChange={(_, newValue) => setTab(newValue)}>
          <BottomNavigationAction label={t('info')} icon={<ArticleIcon />} />
          <BottomNavigationAction label={t('ingredients')} icon={<EggAltIcon />} />
          <BottomNavigationAction label={t('instructions')} icon={<RestaurantIcon />} />
        </BottomNavigation>
      </Paper>

      {/* Delete confirm dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{t('deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('deleteDialogContent')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='contained' color='error' onClick={deleteRecipe}>{t('delete')}</Button>
          <Button onClick={() => setConfirmDialogOpen(false)}>{t('cancel')}</Button>
        </DialogActions>
      </Dialog>

      <ShareRecipeDialog
        open={shareDialogOpen}
        setOpen={setShareDialogOpen}
        recipeId={recipeId!}
        sharedUsers={sharedUsers}
        setSharedUsers={setSharedUsers}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {t('deletionFailed')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RecipeDetails;
